import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, X } from 'lucide-react';
import React, { useCallback, useEffect, useId, useRef } from 'react';
import { shouldReduceMotion } from 'src/tef/components/config/animationConfig';
import { ModalBackdrop } from 'src/tef/components/overlay/ModalBackdrop';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

interface ModalDialogProps extends ForwardedHTMLProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  headerActions?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  size?: 'xs' | ComponentSize | '2xl' | '3xl' | 'full';
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  loading?: boolean;
  preventClose?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
  className?: string;
}

const sizeClasses: Record<string, string> = {
  xs: 'max-w-xs',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  full: 'max-w-[calc(100%-2rem)]',
};

const ModalDialogInner = React.forwardRef<HTMLDivElement, ModalDialogProps>(
  (
    {
      isOpen,
      onClose,
      title,
      description,
      headerActions,
      footer,
      children,
      size = 'md',
      showCloseButton = true,
      closeOnBackdrop = true,
      closeOnEscape = true,
      loading = false,
      preventClose = false,
      initialFocusRef,
      className,
      ...props
    },
    _ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);
    const modalRef = useRef<HTMLDivElement>(null);
    const reduceMotion = shouldReduceMotion();
    const uniqueId = useId();
    const titleId = `modal-title-${uniqueId}`;
    const descId = `modal-desc-${uniqueId}`;

    const canClose = !preventClose && !loading;

    const handleClose = useCallback(() => {
      if (canClose) {
        onClose();
      }
    }, [canClose, onClose]);

    const handleBackdropClick = useCallback(() => {
      if (closeOnBackdrop && canClose) {
        onClose();
      }
    }, [closeOnBackdrop, canClose, onClose]);

    useEffect(() => {
      if (isOpen) {
        const focusElement = initialFocusRef?.current || modalRef.current;
        if (focusElement) {
          setTimeout(() => focusElement.focus(), 0);
        }
      }
    }, [isOpen, initialFocusRef]);

    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && isOpen && closeOnEscape && canClose) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
          document.removeEventListener('keydown', handleEscape);
          document.body.style.overflow = prevOverflow;
        };
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen, onClose, closeOnEscape, canClose]);

    useEffect(() => {
      if (!isOpen) return;

      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const modal = modalRef.current;
        if (!modal) return;

        const focusable = modal.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      document.addEventListener('keydown', handleTab);
      return () => document.removeEventListener('keydown', handleTab);
    }, [isOpen]);

    const modalVariants = reduceMotion
      ? {
          hidden: { opacity: 0, scale: 1 },
          visible: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 1 },
        }
      : {
          hidden: { opacity: 0, scale: 0.95, y: 20 },
          visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          },
          exit: {
            opacity: 0,
            scale: 0.95,
            y: 20,
            transition: { duration: 0.2 },
          },
        };

    return (
      <>
        <ModalBackdrop isOpen={isOpen} onClick={handleBackdropClick} />
        <AnimatePresence>
          {isOpen && (
            <section
              className="fixed inset-0 z-[100001] flex items-center justify-center p-4 pointer-events-none"
              aria-hidden={!isOpen}
            >
              <motion.article
                ref={modalRef as React.Ref<HTMLElement>}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                className={cn(
                  'relative w-full pointer-events-auto',
                  sizeClasses[size],
                  'bg-white dark:bg-slate-800',
                  'border border-slate-200 dark:border-slate-700',
                  'rounded-xl',
                  'shadow-lg',
                  'max-h-[90vh]',
                  'flex flex-col',
                  loading && 'pointer-events-none',
                  className
                )}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                aria-describedby={description ? descId : undefined}
                aria-busy={loading}
                tabIndex={-1}
                {...forwardedProps}
                {...props}
              >
                {loading && (
                  <span className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-800/50 rounded-xl">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" aria-hidden="true" />
                    <span className="sr-only">Loading...</span>
                  </span>
                )}

                {(title || headerActions || showCloseButton) && (
                  <header
                    className={cn(
                      'flex items-start justify-between px-6 py-4',
                      (footer || children) && 'border-b',
                      tef.colors.border
                    )}
                  >
                    <span className="flex-1 min-w-0">
                      {title && (
                        <span
                          id={titleId}
                          className={cn('block', tef.typography.textXl, 'font-semibold', tef.colors.foreground)}
                        >
                          {title}
                        </span>
                      )}
                      {description && (
                        <span
                          id={descId}
                          className={cn('block mt-1', tef.typography.textSm, tef.colors.foregroundSecondary)}
                        >
                          {description}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 ml-4 flex-shrink-0">
                      {headerActions}
                      {showCloseButton && (
                        <button
                          type="button"
                          onClick={handleClose}
                          disabled={!canClose}
                          className={cn(
                            'p-1.5 rounded-lg',
                            tef.colors.muted,
                            'hover:bg-slate-100 dark:hover:bg-slate-700',
                            'hover:text-slate-900 dark:hover:text-slate-100',
                            'transition-colors',
                            'focus:outline-none focus:ring-2 focus:ring-primary/50',
                            !canClose && 'opacity-50 cursor-not-allowed'
                          )}
                          aria-label="Close modal"
                        >
                          <X className="w-5 h-5" aria-hidden="true" />
                        </button>
                      )}
                    </span>
                  </header>
                )}

                <section
                  className={cn(
                    'flex-1 overflow-y-auto overflow-x-hidden min-w-0 px-6',
                    !title && !headerActions && !showCloseButton && 'pt-6',
                    !footer && 'pb-6',
                    footer && 'pb-4'
                  )}
                >
                  {children}
                </section>

                {footer && (
                  <footer className={cn('flex items-center justify-end gap-3 px-6 py-4 border-t', tef.colors.border)}>
                    {footer}
                  </footer>
                )}
              </motion.article>
            </section>
          )}
        </AnimatePresence>
      </>
    );
  }
);

ModalDialogInner.displayName = 'ModalDialog';

export default ModalDialogInner;
export { ModalDialogInner as ModalDialog };
